import React, { useEffect, useRef, useState } from 'react';
import { camBasis } from '../../services/worldObjects';
import { useWorldStore, ZOOM } from '../../state/worldStore';
import { TOWN_LOCATIONS } from '../../rooms';
import { SLICE_DISTRICT } from '../../config';
import { blip } from '../sfx';
import { ensureHudFont, HUD_FONT } from './hudFont';

// ── WHERE YOU ARE ────────────────────────────────────────────────────────────
//
// Walk up to a venue and its name lands in the corner in big type, then lets
// go — the open-world way of saying "you have arrived somewhere" without a
// menu, a pop-up or a stop. The district itself is announced the same way when
// you first set foot on the street.
//
// Polled at 4 Hz off the published camera position; no per-frame React work.

const NEAR = 15;        // world units — close enough to be "at" a venue
const REARM = 26;       // must walk this far away before it can announce again
const HOLD_MS = 4200;

export default function ZoneCard({ touch = false }) {
  const [card, setCard] = useState(null);   // { title, sub, accent, id }
  const zoom = useWorldStore((s) => s.zoom);
  const arrivedAt = useWorldStore((s) => s.arrivedAt);
  const ride = useWorldStore((s) => s.ride);
  const armed = useRef({});
  const hideT = useRef(0);

  useEffect(() => { ensureHudFont(); }, []);

  const show = (c) => {
    clearTimeout(hideT.current);
    setCard({ ...c, id: Date.now() });
    blip(660, 0.6);
    hideT.current = setTimeout(() => setCard(null), HOLD_MS);
  };

  // The district, once, when your feet first touch the street.
  const announced = useRef(false);
  useEffect(() => {
    if (!arrivedAt || announced.current || zoom !== ZOOM.STREET) return undefined;
    announced.current = true;
    const t = setTimeout(() => show({ title: SLICE_DISTRICT.name || 'HypeTown', sub: SLICE_DISTRICT.city || 'Oakland', accent: '#ffb25e' }), 900);
    return () => clearTimeout(t);
  }, [arrivedAt, zoom]);

  useEffect(() => {
    if (zoom !== ZOOM.STREET) return undefined;
    const id = setInterval(() => {
      const anchors = useWorldStore.getState().venueAnchors;
      if (!anchors || useWorldStore.getState().ride) return;
      for (const loc of TOWN_LOCATIONS) {
        const a = anchors[loc.key];
        if (!a) continue;
        const d = Math.hypot(a[0] - camBasis.px, a[2] - camBasis.pz);
        if (d > REARM) armed.current[loc.key] = true;
        else if (d < NEAR && armed.current[loc.key] !== false) {
          armed.current[loc.key] = false;
          show({ title: loc.name, sub: SLICE_DISTRICT.name || 'HypeTown', accent: loc.accent });
          break;
        }
      }
    }, 250);
    return () => clearInterval(id);
  }, [zoom]);

  useEffect(() => () => clearTimeout(hideT.current), []);

  if (!card || zoom !== ZOOM.STREET || ride) return null;
  return (
    <div
      key={card.id}
      style={{
        position: 'absolute',
        ...(touch
          ? { left: '50%', top: 'calc(max(16px, env(safe-area-inset-top)) + 118px)', transform: 'translateX(-50%)', textAlign: 'center', whiteSpace: 'nowrap' }
          : { right: 'max(92px, env(safe-area-inset-right))', bottom: 104, textAlign: 'right' }),
        pointerEvents: 'none',
        animation: `htZoneIn ${HOLD_MS}ms ease forwards`,
      }}
    >
      <style>{`
        @keyframes htZoneIn {
          0% { opacity: 0; transform: ${touch ? 'translate(-50%, 6px)' : 'translateY(6px)'}; filter: blur(4px); }
          9% { opacity: 1; transform: ${touch ? 'translate(-50%, 0)' : 'translateY(0)'}; filter: blur(0); }
          82% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
      <div style={{
        font: `${touch ? 28 : 54}px/0.95 ${HUD_FONT}`, color: '#fff', letterSpacing: 0.5,
        textTransform: 'uppercase', fontStyle: 'italic',
        textShadow: '0 0 1px #000, 2px 2px 0 #000, -1px -1px 0 #000, 0 6px 24px rgba(0,0,0,0.6)',
      }}>{card.title}</div>
      <div style={{
        marginTop: 6, font: `700 ${touch ? 11 : 13}px system-ui, sans-serif`, letterSpacing: 4,
        textTransform: 'uppercase', color: card.accent, textShadow: '1px 1px 0 #000, 0 2px 10px rgba(0,0,0,0.7)',
      }}>{card.sub}</div>
    </div>
  );
}
